/**
 * Application Sidebar Component
 * RiskMetrica Views - Board Risk Committee Control Centre
 *
 * Fixed left navigation sidebar with Committee Core and Risk Domains sections
 */

import { Link, useLocation } from 'react-router-dom';
import { useNavigationStore } from '@/stores/navigationStore';
import { NAVIGATION_CONFIG } from '@/config/navigationConfig';
import { cn } from '@/lib/utils';

export function Sidebar() {
  const location = useLocation();
  const { sidebarOpen } = useNavigationStore();

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  if (!sidebarOpen) {
    return (
      <aside
        className="fixed left-0 bg-slate-900 border-r border-slate-800 z-30"
        style={{
          top: 'var(--header-height)',
          bottom: 0,
          width: '64px',
        }}
      >
        <div className="h-full flex flex-col">
          {/* Collapsed Navigation */}
          <div className="flex-1 py-4 space-y-1">
            {NAVIGATION_CONFIG.map((section) => (
              <div key={section.id} className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.path);
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={cn(
                        'flex items-center justify-center h-12 mx-2 rounded transition-colors',
                        isActive
                          ? 'bg-blue-900/20 text-blue-400'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-slate-100'
                      )}
                      title={item.label}
                    >
                      <Icon className="w-5 h-5" />
                    </Link>
                  );
                })}
                {section.id === 'committee-core' && (
                  <div className="h-px bg-slate-800 mx-3 my-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="fixed left-0 bg-slate-900 border-r border-slate-800 z-30"
      style={{
        top: 'var(--header-height)',
        bottom: 0,
        width: 'var(--sidebar-width)',
      }}
    >
      <div className="h-full flex flex-col">
        {/* Navigation Sections */}
        <nav className="flex-1 py-4 px-3 space-y-6 overflow-y-auto scrollbar-thin">
          {NAVIGATION_CONFIG.map((section) => (
            <div key={section.id}>
              {/* Section Label */}
              <div className="px-3 mb-2">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {section.label}
                </h3>
              </div>

              {/* Section Items */}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.path);
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded transition-colors group',
                        isActive
                          ? 'bg-blue-900/20 text-blue-400 font-medium'
                          : 'text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                      )}
                    >
                      <Icon
                        className={cn(
                          'w-5 h-5 flex-shrink-0',
                          isActive
                            ? 'text-blue-400'
                            : 'text-slate-500 group-hover:text-slate-300'
                        )}
                      />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
}
